<script setup lang="ts">
/**
 * Geöffneter Forum-Post nach aktuellem Client (aus echten Screenshots):
 * Header nur mit fettem Titel + „…"/„×" rechts, Nachrichtenbereich mit
 * Scroll-Wrapper (überlaufender Inhalt kann echt oder choreografiert
 * gescrollt werden), pill-runde Eingabezeile „Message in "…"" fix am
 * unteren Rand; der Typing-Indikator schwebt darüber und nimmt keinen Platz.
 */
withDefaults(defineProps<{
  title: string
  placeholder?: string
}>(), {
  placeholder: undefined
})
</script>

<template>
  <div class="dc-thread">
    <div class="dc-thread-header">
      <span class="dc-thread-title">{{ title }}</span>
      <div class="dc-thread-header-icons" aria-hidden="true">
        <UIcon name="i-lucide-ellipsis" class="size-[18px]" />
        <UIcon name="i-lucide-x" class="size-[18px]" />
      </div>
    </div>

    <div class="dc-thread-messages">
      <div class="dc-scroll">
        <slot />
      </div>
    </div>

    <div class="dc-thread-footer" aria-hidden="true">
      <div class="dc-thread-input">
        <UIcon name="i-lucide-circle-plus" class="dc-thread-input-plus size-[20px]" aria-hidden="true" />
        <span class="dc-thread-input-placeholder">{{ placeholder ?? `Message in "${title}"` }}</span>
        <div class="dc-thread-input-icons" aria-hidden="true">
          <UIcon name="i-lucide-smile" class="size-[19px]" />
          <UIcon name="i-lucide-shapes" class="size-[19px]" />
        </div>
      </div>
      <!-- Typing-Indikator als Overlay ÜBER der Eingabezeile (in der Luft
           unter der letzten Nachricht) — reserviert keinen Platz, damit die
           Eingabezeile immer am unteren Rand der Box sitzt -->
      <div v-if="$slots.typing" class="dc-thread-typing">
        <slot name="typing" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.dc-thread {
  display: flex;
  flex-direction: column;
  background: var(--dc-bg-chat);
  border: 1px solid var(--dc-hairline);
  border-radius: var(--radius-md);
  overflow: hidden;
  font-family: var(--font-discord);
  box-shadow: var(--shadow-float);
}

.dc-thread-header {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 46px;
  padding: 8px 16px;
  border-bottom: 1px solid var(--dc-hairline);
  flex-shrink: 0;
}

.dc-thread-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--dc-ink-header);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dc-thread-header-icons {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 14px;
  color: var(--dc-ink-channel);
  flex-shrink: 0;
}

.dc-thread-messages {
  flex: 1;
  padding: 10px 0 26px; /* großzügige Luft zwischen letzter Nachricht und Input */
  overflow: hidden;
  position: relative;
}

/* .dc-scroll: kein dauerhaftes will-change — GSAP promotet die Liste nur
   während des Fake-Scroll-Tweens (force3D) */

.dc-thread-footer {
  position: relative;
  padding: 0 14px 10px;
  flex-shrink: 0;
}

.dc-thread-typing {
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 100%;
  pointer-events: none;
}

.dc-thread-input {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--dc-bg-input);
  border: 1px solid var(--dc-border-card);
  border-radius: 24px;
  padding: 10px 16px;
}

.dc-thread-input-plus {
  color: var(--dc-ink-channel);
  flex-shrink: 0;
}

.dc-thread-input-placeholder {
  flex: 1;
  font-size: 0.92rem;
  color: var(--dc-ink-channel);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dc-thread-input-icons {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--dc-ink-channel);
}
</style>
